import { Component, inject, Input, signal, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { ToastService } from '../../../core/services/toast.service';
import { VendorsService } from '../../../core/services/vendors.service';
import { DirtyTrackingService } from '../../../core/services/dirty-tracking.service';
import { authActions } from '../../../core/store/auth';
import { ApiError } from '../../../core/models/common/api-error';
import { User } from '../../../core/models/entities/user';
import { FilePondOptions } from 'filepond';
import { FileUploadComponent } from '../../../shared-manager/components/file-upload/file-upload.component';
import { SharedModule } from '../../../shared/shared.module';
import { SharedManagerModule } from '../../../shared-manager/shared-manager.module';

@Component({
  selector: 'app-vendor-logo-settings',
  template: `
    <app-fieldset-card
      [showSaveButton]="true"
      [saving]="saving()"
      [saveButtonDisabled]="!hasChanges()"
      (onSave)="save()"
    >
      <div title>Logo</div>
      <div description>
        Votre logo s'affichera sur votre page publique, les e-mails de
        confirmation et les factures.
      </div>

      <app-file-upload
        #logoUploadCmp
        [config]="logoUploadConfig"
        [defaultFiles]="authUser?.vendor?.logo_url ? [authUser?.vendor?.logo_url] : []"
        (afterAddFile)="onFileChange()"
        (afterRemoveFile)="onFileChange()"
      ></app-file-upload>
    </app-fieldset-card>
  `,
  imports: [SharedModule, SharedManagerModule],
})
export class VendorLogoSettingsComponent {
  @Input() authUser!: User;

  @ViewChild('logoUploadCmp') logoUploadCmp: FileUploadComponent;

  private store = inject(Store);
  private toastService = inject(ToastService);
  private vendorsService = inject(VendorsService);
  private dirtyTrackingService = inject(DirtyTrackingService);

  saving = signal<boolean>(false);
  hasChanges = signal<boolean>(false);

  readonly logoUploadConfig: Partial<FilePondOptions> = {
    className: 'single-col',
    allowMultiple: false,
    maxFileSize: '1MB',
    acceptedFileTypes: [
      'image/png',
      'image/jpeg',
      'image/svg+xml',
      'image/webp',
    ],
    labelIdle: `
      <div>
        <i class="fal fa-file-image"></i>
        <div class="font-sans text-secondary-900">Faites glisser votre logo ici <br>ou <span class="underline cursor-pointer">téléchargez-le depuis votre appareil</span>.</div>
      </div>
    `,
  };

  onFileChange() {
    const hadFile = !!this.authUser?.vendor?.logo_url;
    const hasFile = this.logoUploadCmp.files.length > 0;
    const isNewFile = hasFile && typeof this.logoUploadCmp.files[0] !== 'string';
    const isDeleted = hadFile && !hasFile;
    const changed = isNewFile || isDeleted;
    this.hasChanges.set(changed);
    this.dirtyTrackingService.setDirty(changed);
  }

  async save() {
    this.saving.set(true);
    try {
      const vendor = await this.vendorsService.update({
        name: this.authUser.vendor.name,
        slug: this.authUser.vendor.slug,
        logo:
          typeof this.logoUploadCmp.files[0] === 'string'
            ? null
            : this.logoUploadCmp.files[0],
        delete_logo: !!(
          this.logoUploadCmp.files.length === 0 && this.authUser.vendor.logo_url
        ),
      });
      this.updateUserStore(vendor);
      this.toastService.success('Logo enregistré.');
      this.hasChanges.set(false);
    } catch (exception) {
      this.toastService.error((exception as ApiError).message);
    } finally {
      this.saving.set(false);
    }
  }

  private updateUserStore(vendor: any) {
    this.store.dispatch(
      authActions.findCurrentUserSuccess({
        user: {
          ...this.authUser,
          vendor: { ...this.authUser.vendor, ...vendor },
        },
      }),
    );
  }
}
